/**
 * @file card-tokens.ts
 *
 * MD3 Expressive Card Elevation & Motion Tokens.
 *
 * Elevation levels translated from:
 * - ElevatedCardTokens.kt
 * - FilledCardTokens.kt
 * - OutlinedCardTokens.kt
 * - Elevation.kt
 *
 * Elevation Levels:
 * - Level 0: 0dp (Flat - "none")
 * - Level 1: 1dp (Slight elevation - Elevated rest)
 * - Level 2: 3dp (Hover / Focus elevation for Elevated, Hover for Filled/Outlined)
 * - Level 3: 6dp (Dragged state elevation)
 * - Level 4: 8dp (Pickers/Popups elevation)
 * - Level 5: 12dp (Modals/Dialogs elevation)
 */

export const SHADOW = {
	level0: "none",
	level1: "0px 1px 2px 0px rgba(0,0,0,.3), 0px 1px 3px 1px rgba(0,0,0,.15)",
	level2: "0px 1px 2px 0px rgba(0,0,0,.3), 0px 2px 6px 2px rgba(0,0,0,.15)",
	level3: "0px 4px 8px 3px rgba(0,0,0,.15), 0px 1px 3px 0px rgba(0,0,0,.3)",
	level4: "0px 6px 10px 4px rgba(0,0,0,.15), 0px 2px 3px 0px rgba(0,0,0,.3)",
	level5: "0px 8px 12px 6px rgba(0,0,0,.15), 0px 4px 4px 0px rgba(0,0,0,.3)",
} as const;

export type ShadowLevel = keyof typeof SHADOW;

export type ElevationLevel = 0 | 1 | 2 | 3 | 4 | 5;

export type CardElevationConfig =
	| ElevationLevel
	| {
			rest?: ElevationLevel | string;
			hover?: ElevationLevel | string;
			pressed?: ElevationLevel | string;
			dragged?: ElevationLevel | string;
	  };

/**
 * Resolves a numeric level (0–5) or custom box-shadow string to an MD3 shadow value.
 */
export function resolveElevationShadow(
	level: ElevationLevel | string | undefined,
): string | undefined {
	if (level === undefined) return undefined;
	if (typeof level === "number") {
		const key = `level${level}` as ShadowLevel;
		return SHADOW[key] ?? SHADOW.level0;
	}
	return level;
}

/**
 * Maps each card variant to its elevation level across interaction states.
 * - Elevated: Rest (Level 1) -> Hover/Focus (Level 2) -> Pressed (Level 1) -> Dragged (Level 3)
 * - Filled: Rest (Level 0) -> Hover/Focus (Level 1) -> Pressed (Level 0) -> Dragged (Level 3)
 * - Outlined: Rest (Level 0) -> Hover/Focus (Level 1) -> Pressed (Level 0) -> Dragged (Level 3)
 */
export const VARIANT_ELEVATION = {
	elevated: {
		rest: SHADOW.level1,
		hover: SHADOW.level2,
		pressed: SHADOW.level1,
		dragged: SHADOW.level3,
		disabled: SHADOW.level1, // ElevatedCardTokens.DisabledContainerElevation = Level1
	},
	filled: {
		rest: SHADOW.level0,
		hover: SHADOW.level1,
		pressed: SHADOW.level0,
		dragged: SHADOW.level3,
		disabled: SHADOW.level0,
	},
	outlined: {
		rest: SHADOW.level0,
		hover: SHADOW.level1,
		pressed: SHADOW.level0,
		dragged: SHADOW.level3,
		disabled: SHADOW.level0,
	},
} as const;

export type CardVariant = keyof typeof VARIANT_ELEVATION;

export interface CardElevationOptions {
	variant?: CardVariant;
	disabled?: boolean;
	draggable?: boolean;
	elevation?: CardElevationConfig;
	disableElevation?: boolean;
	hoverEffect?: boolean;
}

/**
 * Hook: Card Elevation Animation
 * Calculates animated boxShadow states based on variant, overrides, and interaction flags.
 */
export function useCardElevation({
	variant = "elevated",
	disabled = false,
	draggable = false,
	elevation,
	disableElevation = false,
	hoverEffect = true,
}: CardElevationOptions) {
	const defaultLevels =
		VARIANT_ELEVATION[variant] || VARIANT_ELEVATION.elevated;

	let restShadow: string = defaultLevels.rest;
	let hoverShadow: string = defaultLevels.hover;
	let pressedShadow: string = defaultLevels.pressed;
	let draggedShadow: string = defaultLevels.dragged;

	if (disableElevation) {
		restShadow = SHADOW.level0;
		hoverShadow = SHADOW.level0;
		pressedShadow = SHADOW.level0;
		draggedShadow = SHADOW.level0;
	} else if (typeof elevation === "number") {
		// When developer supplies a fixed numeric elevation (e.g. elevation={0} or elevation={2}):
		const resolved = resolveElevationShadow(elevation) ?? SHADOW.level0;
		restShadow = resolved;
		// If hover effect is enabled and elevation was provided as a base number,
		// hover either bumps by 1 level (if below max) or stays at given elevation if hoverEffect=false.
		const hoverLevel = hoverEffect
			? (Math.min((elevation as number) + 1, 5) as ElevationLevel)
			: (elevation as ElevationLevel);
		hoverShadow = resolveElevationShadow(hoverLevel) ?? resolved;
		pressedShadow = resolved;
		draggedShadow =
			resolveElevationShadow(
				Math.min((elevation as number) + 2, 5) as ElevationLevel,
			) ?? resolved;
	} else if (elevation && typeof elevation === "object") {
		if (elevation.rest !== undefined) {
			restShadow = resolveElevationShadow(elevation.rest) ?? restShadow;
		}
		if (elevation.hover !== undefined) {
			hoverShadow = resolveElevationShadow(elevation.hover) ?? hoverShadow;
		}
		if (elevation.pressed !== undefined) {
			pressedShadow =
				resolveElevationShadow(elevation.pressed) ?? pressedShadow;
		}
		if (elevation.dragged !== undefined) {
			draggedShadow =
				resolveElevationShadow(elevation.dragged) ?? draggedShadow;
		}
	}

	if (!hoverEffect) {
		hoverShadow = restShadow;
	}

	const animateShadow = disabled
		? disableElevation
			? SHADOW.level0
			: defaultLevels.disabled
		: restShadow;

	return {
		animate: { boxShadow: animateShadow },
		whileHover:
			disabled || !hoverEffect ? undefined : { boxShadow: hoverShadow },
		whileTap: disabled ? undefined : { boxShadow: pressedShadow },
		whileFocus:
			disabled || !hoverEffect ? undefined : { boxShadow: hoverShadow },
		whileDrag:
			!disabled && draggable ? { boxShadow: draggedShadow } : undefined,
		transition: {
			boxShadow: {
				// Incoming: 120ms (from Elevation.kt DefaultIncomingSpec)
				duration: 0.12,
				ease: [0.4, 0, 0.2, 1] as [number, number, number, number],
			},
		},
	};
}
